import "../css/sellermanagement.css"; // Ensure this path is correct
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box, // Re-import Box for the header card
  Typography, // Re-import Typography for the header card
  Button,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { deleteAPI, getWithTokenAPI } from "../../utils/Api";
import { useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

// Define colors used in the component
const primaryGreen = "#DC3545";
const lightGreen = "#ffe5e5";
const secondaryTextColor = "#616161"; // Based on your user table's th color

function RoleManagement() {
  const [rolesdata, setRolesdata] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

  const fetchRoledata = async () => {
    const token = localStorage.getItem("token");
    try {
      setLoading(true);
      const response = await getWithTokenAPI(token, "/rolemanagement/find-all");
      if (response.statusCode === 200) {
        setRolesdata(response.data.roles);
      } else {
        toast.error(response.message || "Error fetching roles");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoledata();
  }, []);

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("No access token available");
        return;
      }
      const payload = { _id: id };
      const response = await deleteAPI(
        token,
        "/rolemanagement/delete",
        payload
      );
      if (response.statusCode === 200) {
        setRolesdata((prev) => prev.filter((role) => role._id !== id));
        toast.success("Role deleted successfully");
      } else {
        toast.error(response.message || "Delete failed");
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete role");
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      {" "}
      {/* Main Box for overall padding and width */}
      <ToastContainer position="top-center" autoClose={1500} />
      {/* Original Header Card Design - Material-UI Box and Typography */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <AdminPanelSettingsIcon
          sx={{ color: primaryGreen, fontSize: 50, mr: 2 }}
        />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Role
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage Roles
          </Typography>
        </Box>
      </Box>
      <div className="d-flex justify-content-end mb-3">
        {permissions.includes("create_role") && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryGreen,
              "&:hover": {
                backgroundColor: "white",
                border: "2px solid #DC3545",
                color: "#DC3545",
              },
              color: "white",
              fontWeight: "bold",
              textTransform: "none",
              width: { xs: "100%", md: "auto" },
              mt: { xs: 2, md: 0 },
            }}
            onClick={() => navigate("/backoffice/rolecreate")}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Role
          </Button>
        )}
      </div>
      {/* Role Table - HTML Table with Bootstrap/Custom CSS */}
      <div className="table-container border rounded">
        <div className="table-responsive">
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress style={{ color: "#DC3545" }} />
              <Typography sx={{ ml: 2, color: primaryGreen }}>
                Loading roles...
              </Typography>
            </Box>
          ) : rolesdata.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                minHeight: '300px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                // border: `1px dashed ${secondaryTextColor || '#6c757d'}`,
                margin: '1rem 0'
              }}
            >
              <AdminPanelSettingsIcon
                style={{
                  fontSize: 80,
                  color: secondaryTextColor || '#6c757d',
                  marginBottom: '1rem',
                  opacity: 0.7
                }}
              />
              <h5
                className="mb-2"
                style={{
                  color: secondaryTextColor || '#495057',
                  fontWeight: 500,
                  fontSize: '1.25rem'
                }}
              >
                No Roles Added Yet
              </h5>
              <button
                className="btn btn-primary"
                style={{
                  backgroundColor:primaryGreen,
                  border: 'none',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '4px',
                  fontWeight: 500
                }}
            onClick={() => navigate("/backoffice/rolecreate")}
              >
                Add New Role
              </button>
            </div>
          ) : (
            <table className="table text-center m-0 resource-head">
              <thead
                className="sticky-header fs-6"
                style={{ backgroundColor: lightGreen }}
              >
                <tr>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    S.No
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Role Name
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Permission Count
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Created At
                  </th>
                  <th
                    className="table-header"
                    style={{ color: secondaryTextColor, textAlign: "left" }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rolesdata.map((role, index) => (
                  <tr key={role._id}>
                    <td style={{ textAlign: "left" }}>{index + 1}</td>
                    <td
                      onClick={() =>
                        navigate(`/backoffice/roleupdate/${role._id}`)
                      }
                      style={{
                        fontWeight: "bold",
                        color: primaryGreen,
                        cursor: "pointer",
                        textDecoration: "none",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.textDecoration = "underline")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.textDecoration = "none")
                      }
                    >
                      {role.role_name || "-"}
                    </td>
                    <td style={{ textAlign: "left" }}>
                      {role.permissions.length} Modules Configured
                    </td>
                    <td style={{ textAlign: "left" }}>
                      {new Date(role.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: "left" }}>
                      <div className="d-flex justify-content-center">
                        {permissions.includes("view_role") && (
                          <span
                            className="action-icon-wrapper"
                            onClick={() =>
                              navigate(`/backoffice/roleupdate/${role._id}`)
                            }
                            title="Edit"
                          >
                            <EditIcon sx={{ color: primaryGreen }} />
                          </span>
                        )}
                        {permissions.includes("view_role") && (
                          <span
                            className="action-icon-wrapper"
                            onClick={() => handleDelete(role._id)}
                            title="Delete"
                          >
                            <DeleteIcon color="error" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Box>
  );
}

export default RoleManagement;
